import { useState, useCallback, useEffect } from 'react';
import { RestrictionType, RestrictionOptions, RestrictionConfig, useFeatureRestriction } from './useFeatureRestriction';

interface RestrictionDialogState {
  isOpen: boolean;
  restrictionType: RestrictionType;
  restrictionConfig: RestrictionConfig;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
}

let globalState: RestrictionDialogState = {
  isOpen: false,
  restrictionType: 'captionTranslation',
  restrictionConfig: {
    heading: '',
    message: '',
    primaryActionTitle: 'OK',
    secondaryActionTitle: 'Contact Sales'
  },
  onPrimaryAction: undefined,
  onSecondaryAction: undefined
};

let listeners: (() => void)[] = [];

const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

export const useGlobalRestrictionDialog = () => {
  const [state, setState] = useState<RestrictionDialogState>(globalState);

  useEffect(() => {
    const listener = () => setState(globalState);
    listeners.push(listener);
    return () => {
      listeners = listeners.filter(l => l !== listener);
    };
  }, []);

  const openRestrictionDialog = useCallback(
    (
      restrictionType: RestrictionType,
      restrictionConfig: RestrictionConfig,
      onPrimaryAction?: () => void,
      onSecondaryAction?: () => void
    ) => {
      globalState = {
        isOpen: true,
        restrictionType,
        restrictionConfig,
        onPrimaryAction,
        onSecondaryAction
      };
      notifyListeners();
    },
    []
  );

  const closeRestrictionDialog = useCallback(() => {
    globalState = {
      ...globalState,
      isOpen: false,
      onPrimaryAction: undefined,
      onSecondaryAction: undefined
    };
    notifyListeners();
  }, []);

  return {
    isOpen: state.isOpen,
    restrictionType: state.restrictionType,
    restrictionConfig: state.restrictionConfig,
    onPrimaryAction: state.onPrimaryAction,
    onSecondaryAction: state.onSecondaryAction,
    openRestrictionDialog,
    closeRestrictionDialog
  };
};

export const useRestrictedFeature = (
  restrictionType: RestrictionType,
  isFeatureEnabled: boolean,
  onFeatureAction: () => void,
  restrictionOptions?: RestrictionOptions
) => {
  const { openRestrictionDialog } = useGlobalRestrictionDialog();
  const { restrictionConfig } = useFeatureRestriction(restrictionType, restrictionOptions);

  const handleAction = useCallback(() => {
    if (isFeatureEnabled) {
      onFeatureAction();
    } else {
      openRestrictionDialog(
        restrictionType,
        restrictionConfig,
        restrictionConfig.onPrimaryAction,
        restrictionConfig.onSecondaryAction
      );
    }
  }, [isFeatureEnabled, onFeatureAction, openRestrictionDialog, restrictionType, restrictionConfig]);

  return {
    handleAction,
    isFeatureEnabled
  };
};
